import { useEffect, useRef, useState } from 'react';
import { useAppel } from '../../appels/useAppel';
import { usePiegeFocus } from '../../lib/usePiegeFocus';

function formaterDuree(debut: number): string {
  const secondes = Math.max(0, Math.floor((Date.now() - debut) / 1000));
  const m = Math.floor(secondes / 60)
    .toString()
    .padStart(2, '0');
  const s = (secondes % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

function useChrono(debut: number): string {
  const [duree, setDuree] = useState(() => formaterDuree(debut));
  useEffect(() => {
    const intervalle = setInterval(() => setDuree(formaterDuree(debut)), 1000);
    return () => clearInterval(intervalle);
  }, [debut]);
  return duree;
}

function IconeMicro({ coupe }: { coupe: boolean }): JSX.Element {
  return (
    <svg aria-hidden="true" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
      {coupe ? (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M3 3l18 18M9 9v3a3 3 0 004.708 2.478M15 9.34V5a3 3 0 00-5.94-.6M5 11a7 7 0 0010.712 5.933M19 11a7 7 0 01-.712 3.07M12 18v3m-4 0h8"
        />
      ) : (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M12 1a3 3 0 00-3 3v7a3 3 0 006 0V4a3 3 0 00-3-3zM19 11a7 7 0 01-14 0M12 18v4m-4 0h8"
        />
      )}
    </svg>
  );
}

function IconeCamera({ coupee }: { coupee: boolean }): JSX.Element {
  return (
    <svg aria-hidden="true" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
      {coupee ? (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M3 3l18 18M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-.324.738M5 5H5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 001.414-.586"
        />
      ) : (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
        />
      )}
    </svg>
  );
}

function IconeRaccrocher(): JSX.Element {
  return (
    <svg aria-hidden="true" className="h-6 w-6 rotate-[135deg]" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
      />
    </svg>
  );
}

function VideoFlux({
  flux,
  muet,
  className,
}: {
  flux: MediaStream | null;
  muet: boolean;
  className: string;
}): JSX.Element {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.srcObject = flux;
  }, [flux]);
  return <video ref={ref} autoPlay playsInline muted={muet} className={className} />;
}

/** Bulle flottante quand l'appel est minimisé — l'appel continue en arrière-plan. */
function BulleAppelEnCours({
  nomContact,
  duree,
  onAgrandir,
  onRaccrocher,
}: {
  nomContact: string;
  duree: string;
  onAgrandir: () => void;
  onRaccrocher: () => void;
}): JSX.Element {
  return (
    <div className="fixed bottom-4 right-4 z-[100] flex items-center gap-3 rounded-full bg-codialis-navy px-4 py-2.5 text-white shadow-card sm:bottom-6 sm:right-6">
      <button
        type="button"
        onClick={onAgrandir}
        className="flex items-center gap-2 text-left"
        aria-label="Rouvrir l'appel en cours"
      >
        <span className="h-2 w-2 animate-pulse rounded-full bg-codialis-mint" />
        <span className="text-sm font-medium">
          {nomContact} · {duree}
        </span>
      </button>
      <button
        type="button"
        onClick={onRaccrocher}
        aria-label="Raccrocher"
        className="flex h-8 w-8 items-center justify-center rounded-full bg-red-600 hover:bg-red-700"
      >
        <IconeRaccrocher />
      </button>
    </div>
  );
}

export function AppelEnCours(): JSX.Element | null {
  const { etat, fluxLocal, fluxDistant, microCoupe, cameraCoupee, raccrocher, minimiser, agrandir, basculerMicro, basculerCamera } =
    useAppel();

  const duree = useChrono(etat.phase === 'en-cours' ? etat.debut : Date.now());
  // Plein écran au clavier (TF-341) : `Échap` réduit l'appel en bulle, il ne
  // raccroche jamais.
  const fenetre = useRef<HTMLDivElement>(null);
  usePiegeFocus(fenetre, etat.phase === 'en-cours' && !etat.minimise, minimiser);

  if (etat.phase !== 'en-cours') return null;

  const nomContact = `${etat.contact.prenom} ${etat.contact.nom}`.trim() || 'Contact';

  if (etat.minimise) {
    return <BulleAppelEnCours nomContact={nomContact} duree={duree} onAgrandir={agrandir} onRaccrocher={raccrocher} />;
  }

  return (
    <div
      ref={fenetre}
      role="dialog"
      aria-modal="true"
      aria-label={`Appel ${etat.typeAppel === 'video' ? 'visio' : 'audio'} avec ${nomContact}`}
      tabIndex={-1}
      className="fixed inset-0 z-[100] flex flex-col bg-codialis-ink text-white focus:outline-none"
    >
      {etat.typeAppel === 'video' ? (
        <div className="relative min-w-0 flex-1 overflow-hidden bg-black">
          <VideoFlux flux={fluxDistant} muet={false} className="h-full w-full object-cover" />
          {!fluxDistant && (
            <div className="absolute inset-0 flex items-center justify-center text-sm text-white/70">
              Connexion en cours…
            </div>
          )}
          {/* Incrustation locale — coin fixe pour l'instant, pas encore déplaçable au doigt. */}
          <div className="absolute bottom-4 right-4 h-28 w-20 overflow-hidden rounded-xl border-2 border-white/20 shadow-lg sm:h-36 sm:w-24">
            {cameraCoupee ? (
              <div className="flex h-full w-full items-center justify-center bg-codialis-navy text-xs text-white/70">
                Caméra coupée
              </div>
            ) : (
              <VideoFlux flux={fluxLocal} muet className="h-full w-full object-cover" />
            )}
          </div>
          <div className="absolute left-4 top-4 rounded-full bg-black/40 px-3 py-1 text-sm">
            {nomContact} · {duree}
          </div>
        </div>
      ) : (
        <div className="flex min-w-0 flex-1 flex-col items-center justify-center gap-4 p-4 sm:p-6">
          <div className="flex h-28 w-28 items-center justify-center rounded-full bg-codialis-navy font-display text-4xl font-bold">
            {etat.contact.initiales}
          </div>
          <div className="font-display text-xl font-bold">{nomContact}</div>
          <div className="text-sm text-white/70">{duree}</div>
          <VideoFlux flux={fluxLocal} muet className="hidden" />
          <VideoFlux flux={fluxDistant} muet={false} className="hidden" />
        </div>
      )}

      <div className="flex items-center justify-center gap-4 bg-black/30 p-4 sm:p-6">
        <button
          type="button"
          onClick={basculerMicro}
          aria-label={microCoupe ? 'Réactiver le micro' : 'Couper le micro'}
          aria-pressed={microCoupe}
          className={`flex h-12 w-12 items-center justify-center rounded-full transition ${
            microCoupe ? 'bg-white text-codialis-navy' : 'bg-white/15 hover:bg-white/25'
          }`}
        >
          <IconeMicro coupe={microCoupe} />
        </button>
        {etat.typeAppel === 'video' && (
          <button
            type="button"
            onClick={basculerCamera}
            aria-label={cameraCoupee ? 'Réactiver la caméra' : 'Couper la caméra'}
            aria-pressed={cameraCoupee}
            className={`flex h-12 w-12 items-center justify-center rounded-full transition ${
              cameraCoupee ? 'bg-white text-codialis-navy' : 'bg-white/15 hover:bg-white/25'
            }`}
          >
            <IconeCamera coupee={cameraCoupee} />
          </button>
        )}
        <button
          type="button"
          onClick={raccrocher}
          aria-label="Raccrocher"
          className="flex h-14 w-14 items-center justify-center rounded-full bg-red-600 hover:bg-red-700 active:scale-95"
        >
          <IconeRaccrocher />
        </button>
        <button
          type="button"
          onClick={minimiser}
          aria-label="Réduire l'appel"
          className="flex h-12 w-12 items-center justify-center rounded-full bg-white/15 hover:bg-white/25"
        >
          <svg aria-hidden="true" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 13H5" />
          </svg>
        </button>
      </div>
    </div>
  );
}
